import React, { useState, useEffect } from "react";
import StepPersonal from "../components/StepPersonal";
import StepGuardian from "../components/StepGuardian";
import StepDocuments   from "../components/StepDocuments";
import StepDeclaration from "../components/StepDeclaration";
import { toast } from "react-toastify";
import API from "../services/api";
import { User, Users, FileText, CheckCircle, X } from "lucide-react";

/* ─── Confirm modal ─────────────────────────────────────────────────────── */
const ConfirmModal = ({ isOpen, onClose, onConfirm, formData }) => {
  if (!isOpen) return null;
  const personal  = formData.personal  || {};
  const guardian  = formData.guardian  || {};
  const documents = formData.documents || {};

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-lg w-full border border-gray-100">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-900">Confirm Your Profile</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition">
            <X size={16} />
          </button>
        </div>
        <p className="text-sm text-gray-500 mb-4">Please review your information before submitting.</p>
        <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-sm border border-gray-200 mb-5">
          {[
            { label: "Full Name",    value: personal.fullName },
            { label: "School Level", value: personal.schoolLevel },
            { label: "Guardian",     value: guardian.guardianName },
            { label: "ID Document",  value: documents.idDocument?.name || "—" },
          ].map(({ label, value }) => (
            <div key={label} className="flex justify-between">
              <span className="text-gray-500">{label}</span>
              <span className="font-medium text-gray-800">{value || "—"}</span>
            </div>
          ))}
        </div>
        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 rounded-xl border-2 border-gray-200 hover:bg-gray-50 text-sm font-medium transition active:scale-95">
            Cancel
          </button>
          <button onClick={onConfirm} className="flex-1 px-4 py-2.5 rounded-xl bg-green-600 text-white hover:bg-green-700 text-sm font-semibold transition active:scale-95">
            Confirm & Submit
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─── MultiStepForm ─────────────────────────────────────────────────────── */
// Steps: 1 Personal → 2 Guardian → 3 Documents → 4 Declaration
// Academic/fees data has been moved to ApplicationForm — this form only
// creates/updates the student profile.
const MultiStepForm = () => {
  const [step, setStep]         = useState(1);
  const [formData, setFormData] = useState({
    personal: {}, guardian: {}, documents: {}, declarationAccepted: false,
  });
  const [isNextDisabled, setIsNextDisabled] = useState(true);
  const [isModalOpen, setIsModalOpen]       = useState(false);
  const [submitting, setSubmitting]         = useState(false);

  /* ── Draft persistence (MultiStepForm is the single owner) ── */
  useEffect(() => {
    const draft = localStorage.getItem("profileDraft");
    if (draft) {
      try { setFormData(JSON.parse(draft)); }
      catch { /* ignore corrupt draft */ }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem("profileDraft", JSON.stringify(formData));
  }, [formData]);

  /* ── Per-step validation ── */
  useEffect(() => {
    const disabled = (() => {
      switch (step) {
        case 1: {
          const p = formData.personal || {};
          return !(
            p.fullName && p.gender && p.dateOfBirth &&
            p.identificationType && p.identificationNumber &&
            p.ward_id && p.location_id && p.sub_location_id &&
            p.village && p.schoolLevel
          );
        }
        case 2: {
          const g = formData.guardian || {};
          return !(g.guardianName && g.ward && g.village);
        }
        case 3: {
          // idDocument required only if the student has an ID number
          const hasId = Boolean(formData.personal?.identificationNumber);
          return hasId && !formData.documents?.idDocument;
        }
        case 4: return !formData.declarationAccepted;
        default: return true;
      }
    })();
    setIsNextDisabled(disabled);
  }, [formData, step]);

  const nextStep = () => setStep((s) => s + 1);
  const prevStep = () => setStep((s) => s - 1);

  const handleSubmit = () => setIsModalOpen(true);

  /* ── Submit profile ── */
  const confirmSubmit = async () => {
    setIsModalOpen(false);
    setSubmitting(true);
    try {
      const { personal, guardian, documents } = formData;

      const data = new FormData();

      data.append("personal", JSON.stringify({
        fullName:             personal.fullName,
        gender:               (personal.gender || "").toUpperCase(),
        dateOfBirth:          personal.dateOfBirth,
        identificationType:   personal.identificationType,
        identificationNumber: personal.identificationNumber,
        ward_id:              personal.ward_id,
        location_id:          personal.location_id,
        sub_location_id:      personal.sub_location_id,
        village:              personal.village || "",
        schoolLevel:          (personal.schoolLevel || "").toUpperCase(),
      }));

      data.append("guardian", JSON.stringify({
        guardianName:  guardian.guardianName  || "",
        guardianId:    guardian.guardianId    || "",
        guardianPhone: guardian.guardianPhone || "",
      }));

      // academic is empty — this form no longer collects it
      data.append("academic", JSON.stringify({}));

      Object.entries(documents || {}).forEach(([key, file]) => {
        if (file instanceof File) data.append(key, file);
      });

      await API.post("/student/profile", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      toast.success("Profile created successfully! You can now apply for a bursary.");
      localStorage.removeItem("profileDraft");
      setStep(1);
      setFormData({ personal: {}, guardian: {}, documents: {}, declarationAccepted: false });

    } catch (err) {
      const msg =
        err.response?.data?.message ||
        (typeof err.response?.data === "string" ? err.response.data : null) ||
        err.message ||
        "Unknown error";
      toast.error(`Submission failed: ${msg}`);
    } finally {
      setSubmitting(false);
    }
  };

  const steps = [
    { label: "Personal Info", icon: User },
    { label: "Guardian Info", icon: Users },
    { label: "Documents",     icon: FileText },
    { label: "Declaration",   icon: CheckCircle },
  ];

  const renderStep = () => {
    switch (step) {
      case 1: return <StepPersonal    formData={formData} setFormData={setFormData} />;
      case 2: return <StepGuardian    formData={formData} setFormData={setFormData} />;
      case 3: return <StepDocuments   formData={formData} setFormData={setFormData} />;
      case 4: return <StepDeclaration formData={formData} setFormData={setFormData} />;
      default: return null;
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-primary">Student Profile Setup</h2>
        <p className="text-gray-500 text-sm mt-1">
          Complete your profile first. You can apply for a bursary once your profile is saved.
        </p>
      </div>

      {/* Step indicators */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm mb-5 p-4 overflow-x-auto">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {steps.map((item, index) => {
            const Icon        = item.icon;
            const s           = index + 1;
            const isCompleted = step > s;
            const isCurrent   = step === s;
            return (
              <div key={item.label} className={`flex flex-col items-center text-center p-3 rounded-xl border transition-colors duration-200 ${
                isCompleted ? "bg-green-50 border-green-200 text-green-700" :
                isCurrent   ? "bg-primary text-white border-primary" :
                              "bg-gray-50 text-gray-400 border-gray-200"
              }`}>
                <div className={`w-9 h-9 flex items-center justify-center rounded-full mb-2 text-sm font-bold ${
                  isCompleted ? "bg-green-600 text-white" :
                  isCurrent   ? "bg-white text-primary" :
                                "bg-gray-200 text-gray-400"
                }`}>
                  {isCompleted ? <CheckCircle size={16} /> : <Icon size={16} />}
                </div>
                <span className="text-xs font-medium">{index + 1}. {item.label}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Progress bar */}
      <div className="flex gap-1 mb-6">
        {[1, 2, 3, 4].map((s) => (
          <div key={s} className={`flex-1 h-1.5 rounded-full transition-colors duration-300 ${step >= s ? "bg-primary" : "bg-gray-200"}`} />
        ))}
      </div>

      {/* Step content */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        {renderStep()}
      </div>

      {/* Navigation */}
      <div className="flex justify-between items-center mt-6 gap-3">
        {step > 1 ? (
          <button onClick={prevStep} className="px-5 py-2.5 rounded-xl border-2 border-gray-200 hover:bg-gray-50 text-sm font-medium transition active:scale-95">
            ← Back
          </button>
        ) : <div />}

        {step < 4 && (
          <button
            onClick={nextStep}
            disabled={isNextDisabled}
            className={`ml-auto px-6 py-2.5 rounded-xl text-sm font-semibold transition active:scale-95 ${
              isNextDisabled
                ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                : "bg-primary text-white hover:bg-blue-800 shadow-sm"
            }`}
          >
            Next Step →
          </button>
        )}

        {step === 4 && (
          <button
            onClick={handleSubmit}
            disabled={isNextDisabled || submitting}
            className={`ml-auto px-6 py-2.5 rounded-xl text-sm font-semibold transition active:scale-95 flex items-center gap-2 ${
              isNextDisabled || submitting
                ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                : "bg-green-600 text-white hover:bg-green-700 shadow-sm"
            }`}
          >
            {submitting && <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
            Save Profile
          </button>
        )}
      </div>

      <ConfirmModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={confirmSubmit}
        formData={formData}
      />
    </div>
  );
};

export default MultiStepForm;